// src/Component/AssociatePartnerCTA.tsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Handshake, X, CheckCircle2, Star, TrendingUp } from 'lucide-react';
import img from '../assets/Algo.jpg'; // Image representing partnership setup

const AssociatePartnerCTA: React.FC = () => {
  // State to manage modal visibility
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 14.APR.2026 Noida Location/Date/Time stamp as requested in prompt context
  const stamp = "LIVE: 14.APR.2026 - NOIDA, INDIA";

  return (
    <>
      {/* <section className="relative w-full py-16 md:py-20  overflow-hidden flex items-center justify-center font-[Urbanist]"> */}
        
        {/* =========================================
            1. BACKGROUND TRADING GLOWS (Red & Green)
            ========================================= */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Subtle grid pattern for trading vibe */}
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
          {/* Bearish Red Glow (Left/Top) */}
          <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-red-900/15 rounded-full blur-[100px] md:blur-[120px]" />
          {/* Bullish Green Glow (Right/Bottom) */}
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-emerald-900/15 rounded-full blur-[100px] md:blur-[120px]" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-8xl">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="flex flex-col lg:flex-row items-stretch justify-between gap-10 lg:gap-12 border border-gray-800 bg-[#0b121f] rounded-3xl shadow-2xl shadow-blue-900/10 overflow-hidden"
          >
            
            {/* =========================================
                LEFT COLUMN: Image with Dual Gradient Overlays
                ========================================= */}
            <div className="w-full lg:w-1/2 relative min-h-[300px] lg:min-h-full">
              {/* Complex Image url representing professional trading environment */}
              <img 
                src= {img} 
                alt="Motilal Algo Partnership Setup" 
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Dual Overlay Gradient (Red/Green Transition) */}
              <div className="absolute inset-0 bg-gradient-to-r from-red-950/90 via-black/40 to-emerald-950/90" />
              {/* Bottom Fade Gradient for text clarity */}
              <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-black/80 to-transparent" />
              
              {/* 3D Pulse Badge over Image */}
              <motion.div 
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-4 -left-4 bg-[#121929]/80 backdrop-blur-xl border border-gray-700 p-4 rounded-2xl shadow-xl flex items-center gap-3 z-20 scale-90 md:scale-100"
              >
                 <TrendingUp className="text-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.8)]" />
                 <div>
                    <span className="text-xs text-slate-500">Live Growth Trajectory</span>
                    <p className="text-base font-extrabold text-white">Associate & Propel</p>
                 </div>
              </motion.div>
            </div>

            {/* =========================================
                RIGHT COLUMN: Trusted Text Content & Button
                ========================================= */}
            <div className="w-full lg:w-1/2 flex flex-col items-center justify-center text-center p-8 md:p-10 lg:p-12 space-y-7">
              <div className="space-y-4">
                <span className="inline-flex items-center gap-2 py-1 px-4 rounded-full bg-emerald-500/10 text-emerald-400 text-xs md:text-sm font-bold tracking-wide border border-emerald-500/20 backdrop-blur-sm">
                  <Star size={16} className="text-amber-400 animate-pulse" /> Unlock Collaborative Success
                </span>
                <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight leading-snug">
                  Partner with a Market Leader <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-emerald-600">Motilal Algo</span>
                </h2>
                <p className="text-base md:text-lg text-slate-400 max-w-xl font-medium leading-relaxed">
                  Join our elite network of associate partners. Leverage our cutting-edge trading technology, robust support infrastructure, and market reputation to enhance your own growth.
                </p>
              </div>

              {/* ACTION BUTTON (Glows and Logo) */}
              <div className="pt-4">
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: "0 0 25px rgba(16,185,129,0.4)" }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsModalOpen(true)} // Open Modal on click
                  className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 text-white px-10 py-4 rounded-2xl text-lg font-bold flex items-center gap-3 transition-all shadow-xl shadow-black/30 group"
                >
                  <Handshake className="text-white group-hover:animate-pulse shadow-[0_0_10px_rgba(255,255,255,1)]" size={24} />
                  Associate Partner
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      {/* </section> */}

      {/* =========================================
          MODAL POPUP SECTION (Smooth Motion AnimatePresence)
          ========================================= */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/80 backdrop-blur-sm font-[Urbanist]"
          >
            {/* Modal Container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-[#0f172a] border border-gray-700 rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col shadow-2xl shadow-blue-900/20 overflow-hidden relative"
            >
              {/* Custom Scrollbar Styles for Modal */}
              <style>{`
                .modal-scroll::-webkit-scrollbar { width: 6px; }
                .modal-scroll::-webkit-scrollbar-track { background: #0f172a; }
                .modal-scroll::-webkit-scrollbar-thumb { background: #334155; border-radius: 10px; }
                .modal-scroll::-webkit-scrollbar-thumb:hover { background: #475569; }
              `}</style>

              {/* Header */}
              <div className="flex items-center justify-between p-5 md:p-6 border-b border-gray-800 bg-[#121b2f] relative overflow-hidden">
                {/* Subtle Glows in Header */}
                <div className="absolute bottom-0 left-0 w-32 h-20 bg-emerald-900/30 rounded-full blur-[60px]" />
                
                <div className="flex items-center gap-3 relative z-10">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-600/20 to-emerald-700/10 text-emerald-400 flex items-center justify-center border border-emerald-500/20">
                    <Handshake className="text-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.8)]" size={20} />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-white tracking-wide">
                    Regulatory & Compliance Disclosures
                  </h3>
                </div>
                <button 
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-full transition-colors relative z-10"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Scrollable Content Body */}
              <div className="p-5 md:p-8 overflow-y-auto modal-scroll text-gray-300 text-sm leading-relaxed space-y-6">
                
                <div>
                  <p className="font-semibold text-white mb-1">Company Details</p>
                  <p><strong>Motilal Algo.:</strong> Member of NSE & BSE – SEBI Registration no.: INZ000061995</p>
                  <p><strong>CDSL:</strong> Depository services through Motilal Algo. – SEBI Registration no.: IN-DP-431-2019</p>
                  <p>Commodity Trading through 46025 – SEBI Registration no.: INZ000141993</p>
                  <p className="mt-2 text-gray-400"><strong>Registered Address:</strong> Kiran Complex, 131/16, Near Pragati Petrol Pump, Zone-II, M.P Nagar, Bhopal, Madhya Pradesh 462011, INDIA.</p>
                </div>

                <div className="bg-emerald-500/5 border border-emerald-500/10 p-4 rounded-xl relative overflow-hidden">
                  {/* subtle glow in block */}
                  <div className="absolute top-1/2 right-0 -translate-y-1/2 w-20 h-10 bg-emerald-900/30 blur-[40px]" />
                  <p className="font-semibold text-emerald-400 mb-2 relative z-10">Complaints & Grievances</p>
                  <p className="relative z-10">For any complaints pertaining to securities broking please contact Motilal Algo. Please ensure you carefully read the Risk Disclosure Document as prescribed by SEBI | ICF Procedure to file a complaint on SEBI SCORES: Register on SCORES portal.</p>
                  <p className="mt-2 relative z-10"><strong>Mandatory details for filing complaints on SCORES:</strong> Name, PAN, Address, Mobile Number, E-mail ID.</p>
                  <p className="relative z-10"><strong>Benefits:</strong> Effective Communication, Speedy redressal of the grievances. Investments in securities market are subject to market risks; read all the related documents carefully before investing.</p>
                </div>

                <div>
                  <p className="font-semibold text-white mb-2">Attention Investors:</p>
                  <ul className="list-disc pl-5 space-y-2 text-gray-400">
                    <li>Stock brokers can accept securities as margins from clients only by way of pledge in the depository system w.e.f September 01, 2020.</li>
                    <li>Update your e-mail and phone number with your stock broker / depository participant and receive OTP directly from depository on your e-mail and/or mobile number to create pledge.</li>
                    <li>Check your securities / MF / bonds in the consolidated account statement issued by NSDL/CDSL every month.</li>
                    <li>"Prevent unauthorised transactions in your account. Update your mobile numbers/email IDs with your stock brokers. Receive information of your transactions directly from Exchange on your mobile/email at the end of the day. Issued in the interest of investors."</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold text-white mb-2">KYC & IPO Information</p>
                  <p>KYC is one time exercise while dealing in securities markets - once KYC is done through a SEBI registered intermediary (broker, DP, Mutual Fund etc.), you need not undergo the same process again when you approach another intermediary.</p>
                  <p className="mt-2">Dear Investor, if you are subscribing to an IPO, there is no need to issue a cheque. Please write the Bank account number and sign the IPO application form to authorize your bank to make payment in case of allotment. In case of non allotment the funds will remain in your bank account.</p>
                </div>

                <div className="border-l-4 border-rose-500 pl-4 py-1 bg-red-950/20 rounded-r-xl">
                  <p className="text-gray-400 italic font-medium">
                    "As a business we don't give stock tips, and have not authorized anyone to trade on behalf of others. If you find anyone claiming to be part of Motilal Algo and offering such services, please report immediately."
                  </p>
                </div>

              </div>

              {/* Footer Actions */}
              <div className="p-5 md:p-6 border-t border-gray-800 bg-[#0b1121] flex justify-between items-center gap-4">
                {/* Subtle location stamp for footer */}
                <div className="flex-shrink-0 flex items-center gap-1.5 text-xs text-gray-700 font-semibold tracking-wide border border-gray-800 px-3 py-1.5 rounded-md">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,1)]" />
                    <span>{stamp}</span>
                </div>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setIsModalOpen(false)}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-3 rounded-xl font-bold flex items-center gap-2 shadow-[0_0_15px_rgba(16,185,129,0.4)] transition-colors"
                >
                  <CheckCircle2 size={20} />
                  I Understood
                </motion.button>
              </div>

            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AssociatePartnerCTA;